// frontend/src/pages/AnnouncementManagement.js
import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Table, Card, Badge, Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';
import api from '../api';

const AnnouncementManagement = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 10
  });

  // 表单状态
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    priority: 1,
    isActive: true
  });
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  // 筛选状态
  const [filterActive, setFilterActive] = useState('all');

  // 获取公告列表
  const fetchAnnouncements = async (page = 1) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pagination.limit.toString()
      });

      if (filterActive !== 'all') {
        params.append('isActive', filterActive);
      }

      const response = await api.get(`/api/announcements?${params.toString()}`);
      setAnnouncements(response.data.announcements);
      setPagination({
        currentPage: response.data.currentPage,
        totalPages: response.data.totalPages,
        totalCount: response.data.totalCount,
        limit: pagination.limit
      });
    } catch (error) {
      console.error('获取公告列表失败:', error);
      toast.error('获取公告列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnnouncements();
  }, [filterActive]);

  // 处理表单提交
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.content.trim()) {
      toast.error('标题和内容不能为空');
      return;
    }

    try {
      const submitFormData = new FormData();
      submitFormData.append('title', formData.title);
      submitFormData.append('content', formData.content);
      submitFormData.append('priority', formData.priority.toString());
      
      if (selectedImage) {
        submitFormData.append('image', selectedImage);
      }

      if (editingId) {
        // 更新公告
        submitFormData.append('isActive', formData.isActive);
        await api.put(`/api/announcements/${editingId}`, submitFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('公告更新成功');
      } else {
        // 创建公告
        await api.post('/api/announcements', submitFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('公告创建成功');
      }

      resetForm();
      setShowModal(false);
      fetchAnnouncements(pagination.currentPage);
    } catch (error) {
      console.error('操作失败:', error);
      toast.error(error.response?.data?.message || '操作失败');
    }
  };

  // 重置表单
  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      priority: 1,
      isActive: true
    });
    setSelectedImage(null);
    setImagePreview(null);
    setEditingId(null);
  };

  // 打开创建模态框
  const handleCreate = () => {
    resetForm();
    setShowModal(true);
  };

  // 打开编辑模态框
  const handleEdit = (announcement) => {
    setFormData({
      title: announcement.title,
      content: announcement.content,
      priority: announcement.priority,
      isActive: announcement.isActive
    });
    setEditingId(announcement._id);
    if (announcement.imageUrl) {
      setImagePreview(`${api.defaults.baseURL}${announcement.imageUrl}`);
    }
    setShowModal(true);
  };

  // 删除公告
  const handleDelete = async (id) => {
    if (!window.confirm('确定要删除这条公告吗？')) return;

    try {
      await api.delete(`/api/announcements/${id}`);
      toast.success('公告删除成功');
      fetchAnnouncements(pagination.currentPage);
    } catch (error) {
      console.error('删除失败:', error);
      toast.error('删除失败');
    }
  };

  // 切换公告状态
  const handleToggleStatus = async (id) => {
    try {
      await api.patch(`/api/announcements/${id}/toggle`);
      toast.success('状态切换成功');
      fetchAnnouncements(pagination.currentPage);
    } catch (error) {
      console.error('状态切换失败:', error);
      toast.error('状态切换失败');
    }
  };

  // 处理图片选择
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // 验证文件类型
      if (!file.type.startsWith('image/')) {
        toast.error('请选择图片文件');
        return;
      }
      
      // 验证文件大小 (5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('图片文件不能超过5MB');
        return;
      }

      setSelectedImage(file);
      
      // 生成预览
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  // 获取优先级显示
  const getPriorityBadge = (priority) => {
    if (priority >= 8) return <Badge bg="danger">高</Badge>;
    if (priority >= 5) return <Badge bg="warning">中</Badge>;
    return <Badge bg="info">低</Badge>;
  };

  return (
    <div className="p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>📢 公告管理</h2>
        <div className="d-flex gap-2 align-items-center">
          <Form.Select 
            value={filterActive} 
            onChange={(e) => setFilterActive(e.target.value)}
            style={{ width: '120px' }}
          >
            <option value="all">全部</option>
            <option value="true">启用</option>
            <option value="false">禁用</option>
          </Form.Select>
          <Button variant="success" onClick={handleCreate}>
            ➕ 新建公告
          </Button>
        </div>
      </div>

      {/* 统计信息 */}
      <Alert variant="info" className="mb-4">
        <strong>📊 统计信息:</strong> 共 {pagination.totalCount} 条公告
      </Alert>

      {/* 公告列表 */}
      <Card>
        <Card.Body>
          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">加载中...</span>
              </div>
            </div>
          ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>标题</th>
                  <th>内容预览</th>
                  <th>图片</th>
                  <th>优先级</th>
                  <th>状态</th>
                  <th>创建者</th>
                  <th>创建时间</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {announcements.map(announcement => (
                  <tr key={announcement._id}>
                    <td>
                      <strong>{announcement.title}</strong>
                    </td>
                    <td>
                      <div className="text-truncate" style={{ maxWidth: '200px' }}>
                        {announcement.content}
                      </div>
                    </td>
                    <td>
                      {announcement.imageUrl ? (
                        <img 
                          src={`${api.defaults.baseURL}${announcement.imageUrl}`}
                          alt="公告图片"
                          style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                          className="rounded"
                        />
                      ) : (
                        <span className="text-muted">无图片</span>
                      )}
                    </td>
                    <td>{getPriorityBadge(announcement.priority)}</td>
                    <td>
                      <Badge bg={announcement.isActive ? 'success' : 'secondary'}>
                        {announcement.isActive ? '启用' : '禁用'}
                      </Badge>
                    </td>
                    <td>{announcement.createdBy?.username || '未知'}</td>
                    <td>{new Date(announcement.createdAt).toLocaleString()}</td>
                    <td>
                      <div className="d-flex gap-1">
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => handleEdit(announcement)}
                        >
                          编辑
                        </Button>
                        <Button
                          size="sm"
                          variant={announcement.isActive ? 'outline-warning' : 'outline-success'}
                          onClick={() => handleToggleStatus(announcement._id)}
                        >
                          {announcement.isActive ? '禁用' : '启用'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => handleDelete(announcement._id)}
                        >
                          删除
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}

          {announcements.length === 0 && !loading && (
            <div className="text-center py-4 text-muted">
              暂无公告数据
            </div>
          )}
        </Card.Body>
      </Card>

      {/* 分页 */}
      {pagination.totalPages > 1 && (
        <div className="d-flex justify-content-center mt-4">
          <div className="d-flex gap-2">
            <Button
              variant="outline-primary"
              disabled={pagination.currentPage <= 1}
              onClick={() => fetchAnnouncements(pagination.currentPage - 1)}
            >
              上一页
            </Button>
            <span className="align-self-center mx-3">
              第 {pagination.currentPage} / {pagination.totalPages} 页
            </span>
            <Button
              variant="outline-primary"
              disabled={pagination.currentPage >= pagination.totalPages}
              onClick={() => fetchAnnouncements(pagination.currentPage + 1)}
            >
              下一页
            </Button>
          </div>
        </div>
      )}

      {/* 编辑/创建模态框 */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingId ? '📝 编辑公告' : '➕ 新建公告'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>公告标题 *</Form.Label>
              <Form.Control
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="请输入公告标题"
                maxLength={100}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>公告内容 *</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="请输入公告内容"
                maxLength={500}
                required
              />
              <Form.Text className="text-muted">
                {formData.content.length}/500 字符
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>公告图片</Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                onChange={handleImageChange}
              />
              <Form.Text className="text-muted">
                支持 JPG、PNG、GIF 格式，文件大小不超过 5MB
              </Form.Text>
              {imagePreview && (
                <div className="mt-2">
                  <img
                    src={imagePreview}
                    alt="预览"
                    style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'cover' }}
                    className="rounded border"
                  />
                </div>
              )}
            </Form.Group>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>优先级</Form.Label>
                  <Form.Select
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
                  >
                    <option value={1}>低 (1)</option>
                    <option value={2}>低 (2)</option>
                    <option value={3}>低 (3)</option>
                    <option value={4}>低 (4)</option>
                    <option value={5}>中 (5)</option>
                    <option value={6}>中 (6)</option>
                    <option value={7}>中 (7)</option>
                    <option value={8}>高 (8)</option>
                    <option value={9}>高 (9)</option>
                    <option value={10}>高 (10)</option>
                  </Form.Select>
                </Form.Group>
              </div>
              {editingId && (
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>状态</Form.Label>
                    <Form.Select
                      value={formData.isActive.toString()}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.value === 'true' })}
                    >
                      <option value="true">启用</option>
                      <option value="false">禁用</option>
                    </Form.Select>
                  </Form.Group>
                </div>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              取消
            </Button>
            <Button variant="primary" type="submit">
              {editingId ? '更新' : '创建'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default AnnouncementManagement;
